import React, { useState, useEffect, useRef } from 'react';
import {
  RiPlayCircleLine,
  RiPauseLine,
  RiSkipBackLine,
  RiSkipForwardLine,
  RiRefreshLine,
} from 'react-icons/ri';

const STEP_MS = 2200;

const STEPS = [
  { id: 1, label: "Query arrives",        description: "Your question reaches Stackform's Orchestrator — the coordinator that plans and routes all work." },
  { id: 2, label: "Concise answer",        description: "The Orchestrator immediately returns a concise answer so you're not left waiting while deeper research begins." },
  { id: 3, label: "Agents dispatched",     description: "In parallel, the Orchestrator dispatches the Generalist (web & news) and Researcher (academic papers) agents." },
  { id: 4, label: "BYOE model calls",      description: "Each agent calls your own models via Bring Your Own Endpoints (BYOE) — OpenAI, Gemini, Claude, Azure, or AWS." },
  { id: 5, label: "Results returned",      description: "Both agents return their findings to the Orchestrator, which synthesizes them into a single coherent response." },
  { id: 6, label: "Expansive report",      description: "The Orchestrator delivers a comprehensive, synthesized report — deeper and more accurate than any single model." },
];

const ACC = {
  blue:   { stroke: '#0288d1', fill: 'rgba(2,136,209,0.13)'   },
  green:  { stroke: '#388e3c', fill: 'rgba(56,142,60,0.13)'   },
  purple: { stroke: '#7b1fa2', fill: 'rgba(123,31,162,0.13)'  },
  orange: { stroke: '#f59e0b', fill: 'rgba(245,158,11,0.13)'  },
};

// Gen↔Orch and Res↔Orch share active steps 3 (dispatch) and 5 (results)
const ACTIVE = {
  'n-you':      [1, 2, 6],
  'n-orch':     [1, 2, 3, 5, 6],
  'n-gen':      [3, 4, 5],
  'n-res':      [3, 4, 5],
  'n-models':   [4],
  'a-query':    [1],
  'a-answer':   [2, 6],
  'a-gen-orch': [3, 5],
  'a-res-orch': [3, 5],
  'a-bg':       [4],
  'a-br':       [4],
};

const N_ACC = {
  'n-you': 'blue', 'n-orch': 'blue',
  'n-gen': 'green', 'n-res': 'purple', 'n-models': 'orange',
};
const A_ACC = {
  'a-query': 'blue', 'a-answer': 'blue',
  'a-gen-orch': 'green', 'a-res-orch': 'purple',
  'a-bg': 'orange', 'a-br': 'orange',
};

export default function OaaSFlowDiagram() {
  const [step, setStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const maxStep = STEPS.length;

  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setStep(prev => {
          if (prev >= maxStep) { setIsPlaying(false); clearInterval(timerRef.current); return prev; }
          return prev + 1;
        });
      }, STEP_MS);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying, maxStep]);

  const on  = (id) => ACTIVE[id]?.includes(step) ?? false;
  const ns  = (id) => on(id) ? ACC[N_ACC[id]].stroke : 'var(--border-color, #ccc)';
  const nf  = (id) => on(id) ? ACC[N_ACC[id]].fill   : 'var(--panel-background, #fff)';
  const nt  = (id) => on(id) ? ACC[N_ACC[id]].stroke : 'var(--text-secondary, #888)';
  const as_ = (id) => on(id) ? ACC[A_ACC[id]].stroke : 'var(--border-color, #ccc)';
  const mk  = (id) => `url(#mo-${on(id) ? A_ACC[id] : 'off'})`;
  const ms  = (id) => `url(#ms-${on(id) ? A_ACC[id] : 'off'})`;

  const handlePlayPause = () => {
    if (step >= maxStep) { setStep(0); setIsPlaying(true); }
    else setIsPlaying(p => !p);
  };

  const currentStepData = step > 0 ? STEPS[step - 1] : null;

  // ── SVG layout (viewBox "0 0 420 370") ──────────────────────────────────
  // You:    x=15,  y=52,  w=105, h=42  → cx=67,  right=120, bottom=94
  // Orch:   x=165, y=48,  w=175, h=52  → cx=252, left=165,  right=340, bottom=100
  // Gen:    x=20,  y=200, w=160, h=48  → cx=100, right=180, top=200,   bottom=248
  // Res:    x=235, y=200, w=160, h=48  → cx=315, left=235,  top=200,   bottom=248
  // Models: x=75,  y=300, w=260, h=52  → cx=205, top=300
  //
  // Bidirectional arrows (single line, markerStart + markerEnd):
  //   Gen↔Orch: (208,100)→(118,200)  — x ranges [118,208], confirmed no cross with Res vertical
  //   Res↔Orch: (288,100)→(288,200)  — parallel vertical, x=288 inside both boxes
  //   Gen↔Models: (112,248)→(158,300) — clean diagonal, no cross with Res↔Models
  //   Res↔Models: (303,248)→(257,300) — clean diagonal, [257,303] vs [112,158] no overlap

  return (
    <div style={{
      border: '1px solid var(--border-color, #ccc)',
      borderRadius: 12, padding: '16px 12px', margin: '1.5rem 0',
      background: 'var(--panel-background, #fff)', userSelect: 'none',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox="0 0 420 370" style={{ width: '100%', maxWidth: 420, display: 'block', margin: '0 auto' }}>
          <defs>
            {['off', 'blue', 'green', 'purple', 'orange'].map(key => {
              const color = key === 'off' ? '#cccccc' : ACC[key].stroke;
              return (
                <React.Fragment key={key}>
                  <marker id={`mo-${key}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                    <path d="M0,0 L0,7 L7,3.5 z" fill={color} />
                  </marker>
                  <marker id={`ms-${key}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto-start-reverse">
                    <path d="M0,0 L0,7 L7,3.5 z" fill={color} />
                  </marker>
                </React.Fragment>
              );
            })}
          </defs>

          {/* ── Arrows ── */}

          {/* You → Orch: query */}
          <path d="M 120,67 L 165,67" fill="none"
            stroke={as_('a-query')} strokeWidth={on('a-query') ? 2.5 : 1.5}
            markerEnd={mk('a-query')} style={{ transition: 'stroke 0.35s' }} />
          <text x={142} y={60} textAnchor="middle" fontSize="10"
            fill={as_('a-query')} style={{ transition: 'fill 0.35s' }}>query</text>

          {/* Orch ← You: answer — label below both boxes (box bottoms y=94/100) */}
          <path d="M 165,82 L 120,82" fill="none"
            stroke={as_('a-answer')} strokeWidth={on('a-answer') ? 2.5 : 1.5}
            markerEnd={mk('a-answer')} style={{ transition: 'stroke 0.35s' }} />
          <text x={142} y={112} textAnchor="middle" fontSize="10"
            fill={as_('a-answer')} style={{ transition: 'fill 0.35s' }}>
            {step === 6 ? 'expansive report' : 'concise answer'}
          </text>

          {/* Gen ↔ Orch: single bidirectional diagonal */}
          <path d="M 208,100 L 118,200" fill="none"
            stroke={as_('a-gen-orch')} strokeWidth={on('a-gen-orch') ? 2.5 : 1.5}
            markerEnd={mk('a-gen-orch')} markerStart={ms('a-gen-orch')}
            style={{ transition: 'stroke 0.35s' }} />
          <text x={155} y={148} textAnchor="end" fontSize="10"
            fill={as_('a-gen-orch')} style={{ transition: 'fill 0.35s' }}>
            {step === 5 ? 'results' : 'dispatch'}
          </text>

          {/* Res ↔ Orch: single bidirectional vertical */}
          <path d="M 288,100 L 288,200" fill="none"
            stroke={as_('a-res-orch')} strokeWidth={on('a-res-orch') ? 2.5 : 1.5}
            markerEnd={mk('a-res-orch')} markerStart={ms('a-res-orch')}
            style={{ transition: 'stroke 0.35s' }} />
          <text x={297} y={148} textAnchor="start" fontSize="10"
            fill={as_('a-res-orch')} style={{ transition: 'fill 0.35s' }}>
            {step === 5 ? 'results' : 'dispatch'}
          </text>

          {/* Gen ↔ Models: vertical from Gen centre down to Models */}
          <path d="M 100,248 L 100,300" fill="none"
            stroke={as_('a-bg')} strokeWidth={on('a-bg') ? 2.5 : 1.5}
            markerEnd={mk('a-bg')} markerStart={ms('a-bg')}
            style={{ transition: 'stroke 0.35s' }} />
          <text x={88} y={277} textAnchor="end" fontSize="10"
            fill={as_('a-bg')} style={{ transition: 'fill 0.35s' }}>BYOE</text>

          {/* Res ↔ Models: vertical from Res centre down to Models */}
          <path d="M 315,248 L 315,300" fill="none"
            stroke={as_('a-br')} strokeWidth={on('a-br') ? 2.5 : 1.5}
            markerEnd={mk('a-br')} markerStart={ms('a-br')}
            style={{ transition: 'stroke 0.35s' }} />
          <text x={327} y={277} textAnchor="start" fontSize="10"
            fill={as_('a-br')} style={{ transition: 'fill 0.35s' }}>BYOE</text>

          {/* ── Nodes (drawn last so they render over arrow endpoints) ── */}

          {/* You */}
          <rect x={15} y={52} width={105} height={42} rx={8}
            fill={nf('n-you')} stroke={ns('n-you')} strokeWidth={on('n-you') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-you') ? `drop-shadow(0 0 5px ${ns('n-you')}88)` : 'none' }} />
          <text x={67} y={73} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-you')} style={{ transition: 'fill 0.35s' }}>🧑 You</text>

          {/* Orchestrator */}
          <rect x={165} y={48} width={175} height={52} rx={8}
            fill={nf('n-orch')} stroke={ns('n-orch')} strokeWidth={on('n-orch') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-orch') ? `drop-shadow(0 0 5px ${ns('n-orch')}88)` : 'none' }} />
          <text x={252} y={67} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-orch')} style={{ transition: 'fill 0.35s' }}>🎼 Orchestrator</text>
          <text x={252} y={85} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">Plans &amp; Routes</text>

          {/* Generalist */}
          <rect x={20} y={200} width={160} height={48} rx={8}
            fill={nf('n-gen')} stroke={ns('n-gen')} strokeWidth={on('n-gen') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-gen') ? `drop-shadow(0 0 5px ${ns('n-gen')}88)` : 'none' }} />
          <text x={100} y={219} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-gen')} style={{ transition: 'fill 0.35s' }}>🌐 Generalist</text>
          <text x={100} y={237} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">web / news</text>

          {/* Researcher */}
          <rect x={235} y={200} width={160} height={48} rx={8}
            fill={nf('n-res')} stroke={ns('n-res')} strokeWidth={on('n-res') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-res') ? `drop-shadow(0 0 5px ${ns('n-res')}88)` : 'none' }} />
          <text x={315} y={219} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-res')} style={{ transition: 'fill 0.35s' }}>🔬 Researcher</text>
          <text x={315} y={237} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">papers / academic</text>

          {/* Your Models (BYOE) */}
          <rect x={75} y={300} width={260} height={52} rx={8}
            fill={nf('n-models')} stroke={ns('n-models')} strokeWidth={on('n-models') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-models') ? `drop-shadow(0 0 5px ${ns('n-models')}88)` : 'none' }} />
          <text x={205} y={320} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-models')} style={{ transition: 'fill 0.35s' }}>🛠️ Your Models</text>
          <text x={205} y={338} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">BYOE — AWS · Claude · Gemini · OpenAI</text>
        </svg>
      </div>

      {/* Step description */}
      <div style={{
        minHeight: 50, margin: '8px 0 6px', padding: '8px 12px',
        borderRadius: 8, background: 'var(--bg-secondary, #f5f5f5)',
        fontSize: '0.82rem', color: 'var(--text-secondary, #555)',
        textAlign: 'center', opacity: currentStepData ? 1 : 0.4,
      }}>
        {currentStepData
          ? <><strong style={{ color: 'var(--text-color, #333)' }}>Step {step}: {currentStepData.label}</strong> — {currentStepData.description}</>
          : 'Press play to walk through how Stackform handles your query.'}
      </div>

      {/* Controls — two fixed rows matching the other tutorials */}
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>

        {/* Row 1: progress dots (left) + status | Reset (right, always present) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => { setIsPlaying(false); setStep(i + 1); }}
                title={s.label}
                style={{
                  width: 10, height: 10, borderRadius: '50%', border: 'none',
                  padding: 0, cursor: 'pointer',
                  background: step === i + 1 ? '#0288d1' : step > i + 1 ? '#0288d155' : 'var(--border-color, #ccc)',
                  transition: 'background 0.3s',
                }}
              />
            ))}
            <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--text-secondary, #888)', whiteSpace: 'nowrap' }}>
              {step === 0 ? 'Ready' : isPlaying ? 'Running…' : step >= maxStep ? 'Complete' : `Step ${step} / ${maxStep} — paused`}
            </span>
          </div>
          <button
            onClick={() => { setIsPlaying(false); setStep(0); }}
            disabled={step === 0}
            title="Reset"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '0.28rem 0.6rem', fontSize: '0.78rem', fontWeight: 500,
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: 6,
              background: 'transparent',
              color: 'var(--text-secondary, #666)',
              cursor: step === 0 ? 'default' : 'pointer',
              opacity: step === 0 ? 0.4 : 1,
            }}
          >
            <RiRefreshLine size={12} /> Reset
          </button>
        </div>

        {/* Row 2: ◀ · Play/Pause · ▶ — always three slots, no position shift */}
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center' }}>
          {/* ◀ Back — slot 1, always present */}
          <button
            onClick={() => { setIsPlaying(false); setStep(s => Math.max(0, s - 1)); }}
            disabled={step === 0}
            title="Previous step"
            style={{
              display: 'flex', alignItems: 'center',
              padding: '0.32rem 0.6rem', fontSize: '0.82rem',
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: 6, background: 'transparent',
              color: step === 0 ? 'var(--text-secondary, #ccc)' : 'var(--text-color, #333)',
              cursor: step === 0 ? 'not-allowed' : 'pointer',
              opacity: step === 0 ? 0.4 : 1,
            }}
          >
            <RiSkipBackLine size={14} />
          </button>

          {/* Centre — slot 2, label/style changes only, position locked */}
          <button
            onClick={handlePlayPause}
            title={step >= maxStep ? 'Replay' : isPlaying ? 'Pause' : step === 0 ? 'Run' : 'Resume'}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '0.32rem 0.85rem', fontSize: '0.82rem', fontWeight: 700,
              border: '1px solid #0288d1',
              borderRadius: 6,
              background: isPlaying ? 'transparent' : '#0288d1',
              color: isPlaying ? '#0288d1' : '#fff',
              cursor: 'pointer',
              minWidth: '5rem',
              transition: 'all 0.15s',
            }}
          >
            {isPlaying ? <RiPauseLine size={14} /> : <RiPlayCircleLine size={14} />}
            {step >= maxStep ? 'Replay' : isPlaying ? 'Pause' : step === 0 ? 'Run' : 'Resume'}
          </button>

          {/* ▶ Next — slot 3, always present */}
          <button
            onClick={() => { setIsPlaying(false); setStep(s => Math.min(maxStep, s + 1)); }}
            disabled={step >= maxStep}
            title="Next step"
            style={{
              display: 'flex', alignItems: 'center',
              padding: '0.32rem 0.6rem', fontSize: '0.82rem',
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: 6, background: 'transparent',
              color: step >= maxStep ? 'var(--text-secondary, #ccc)' : 'var(--text-color, #333)',
              cursor: step >= maxStep ? 'not-allowed' : 'pointer',
              opacity: step >= maxStep ? 0.4 : 1,
            }}
          >
            <RiSkipForwardLine size={14} />
          </button>
        </div>

      </div>
    </div>
  );
}
